import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, UserPlus, Trash2, Shield, Loader2, Users } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { getAuthHeaders } from "@/lib/auth-helper";
import {
  listEventStaff,
  addEventStaff,
  updateEventStaffRole,
  removeEventStaff,
} from "@/lib/staff.functions";

export const Route = createFileRoute("/dashboard/events/$eventId/staff")({
  head: () => ({
    meta: [{ title: "Staff del evento — ZYNTRA" }],
  }),
  component: EventStaffPage,
});

type StaffRow = {
  id: string;
  eventId: string;
  userId: string;
  role: string;
  createdAt: string;
  displayName: string | null;
  email: string | null;
};

function EventStaffPage() {
  const { eventId } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState<{ id: string; title: string } | null>(null);
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"scanner" | "manager">("scanner");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
    if (user) load();
  }, [user, loading, eventId]);

  async function load() {
    setLoadingData(true);
    setError("");
    try {
      const { data: ev } = await supabase
        .from("events")
        .select("id, title, organizer_id")
        .eq("id", eventId)
        .single();
      if (!ev || ev.organizer_id !== user?.id) {
        // Not the organizer — back to dashboard (admins use a separate flow)
        navigate({ to: "/dashboard" });
        return;
      }
      setEvent({ id: ev.id, title: ev.title });
      const headers = await getAuthHeaders();
      const res = await listEventStaff({ data: { eventId }, headers } as any);
      setStaff(res.staff as StaffRow[]);
    } catch (e: any) {
      setError(e.message || "Error al cargar staff");
    } finally {
      setLoadingData(false);
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      await addEventStaff({ data: { eventId, email: email.trim(), role }, headers } as any);
      setEmail("");
      setRole("scanner");
      setSuccess("Staff agregado correctamente.");
      await load();
    } catch (e: any) {
      setError(e.message || "No se pudo agregar el staff");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRoleChange(staffId: string, newRole: "scanner" | "manager") {
    try {
      const headers = await getAuthHeaders();
      await updateEventStaffRole({ data: { staffId, role: newRole }, headers } as any);
      setStaff((prev) => prev.map((s) => (s.id === staffId ? { ...s, role: newRole } : s)));
    } catch (e: any) {
      setError(e.message || "No se pudo actualizar el rol");
    }
  }

  async function handleRemove(staffId: string) {
    if (!confirm("¿Eliminar este staff del evento?")) return;
    try {
      const headers = await getAuthHeaders();
      await removeEventStaff({ data: { staffId }, headers } as any);
      setStaff((prev) => prev.filter((s) => s.id !== staffId));
    } catch (e: any) {
      setError(e.message || "No se pudo eliminar");
    }
  }

  if (loading || loadingData) {
    return (
      <div className="mx-auto flex max-w-2xl items-center justify-center px-4 py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pb-20 pt-6 sm:px-6">
      <Link
        to="/dashboard"
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver al dashboard
      </Link>

      <div className="mb-6 flex items-start gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl gradient-primary">
          <Users className="h-6 w-6 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-xl font-black text-foreground">Staff del evento</h1>
          <p className="text-sm text-muted-foreground">{event?.title}</p>
        </div>
      </div>

      {/* Add staff */}
      <form onSubmit={handleAdd} className="mb-6 rounded-2xl border border-border bg-card p-5">
        <h2 className="mb-3 text-sm font-bold text-foreground">Agregar staff</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Email del usuario</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@ejemplo.com"
              className="mt-1.5 w-full rounded-lg border border-border bg-input px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Rol</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as "scanner" | "manager")}
              className="mt-1.5 w-full rounded-lg border border-border bg-input px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="scanner">Scanner (validar tickets en puerta)</option>
              <option value="manager">Manager (apoyo operativo)</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={submitting || !email.trim()}
            className="flex w-full items-center justify-center gap-2 rounded-xl gradient-primary py-3 text-sm font-bold text-primary-foreground transition-all hover:opacity-90 disabled:opacity-40"
          >
            <UserPlus className="h-4 w-4" />
            {submitting ? "Agregando..." : "Agregar staff"}
          </button>
        </div>
      </form>

      {error && (
        <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 rounded-xl border border-success/30 bg-success/10 p-3 text-sm text-success">
          {success}
        </div>
      )}

      {/* Staff list */}
      <div className="rounded-2xl border border-border bg-card">
        <div className="border-b border-border p-4">
          <h2 className="text-sm font-bold text-foreground">Staff asignado ({staff.length})</h2>
        </div>
        {staff.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Aún no asignaste staff a este evento.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {staff.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-3 p-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary">
                  <Shield className="h-4 w-4 text-secondary-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {s.displayName || s.email || s.userId.slice(0, 8)}
                  </p>
                  {s.email && <p className="truncate text-xs text-muted-foreground">{s.email}</p>}
                </div>
                <select
                  value={s.role}
                  onChange={(e) => handleRoleChange(s.id, e.target.value as "scanner" | "manager")}
                  className="rounded-lg border border-border bg-input px-2 py-1.5 text-xs text-foreground"
                >
                  <option value="scanner">Scanner</option>
                  <option value="manager">Manager</option>
                </select>
                <button
                  onClick={() => handleRemove(s.id)}
                  className="rounded-lg border border-destructive/30 bg-destructive/10 p-2 text-destructive hover:bg-destructive/20"
                  aria-label="Eliminar"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
